import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, Link as MuiLink, IconButton } from '@mui/material';
import Coordinates from 'components/Coordinates';
import ResponsiveKeyValueList from 'components/ResponsiveKeyValueList';
import SimpleDialog from 'core/components/Dialogs/SimpleDialog';
import { getFormattedRuPhone } from 'core/utils/commonUtils';
import { getGeolocationLink } from 'core/utils/orderUtils';
import { compact, isEmpty, isNil } from 'lodash';
import { useMemo, type FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { getActionUserRoleName } from './utils';
import type { EventExtendedProps } from './CalendarPage';

const EventDetailDialog: FC<EventDetailDialogProps> = ({ event, open, onClose }) => {
  const orderPath = useMemo(() => {
    const paths = ['/requests', '/orders', '/archive'] as const;

    return `${paths[event.orderState]}/${event.orderId}`;
  }, [event]);

  const rows = useMemo(
    () =>
      compact([
        { label: 'Этап', value: event.action },
        { label: 'Дата', value: event.dateAction },
        {
          label: 'Номер заказа',
          value: (
            <MuiLink component={RouterLink} to={orderPath}>
              {event.orderNumber}
            </MuiLink>
          ),
        },
        !isNil(event.userId) && {
          label: getActionUserRoleName(event.actionCode),
          value: event.userFullName,
        },
        !isEmpty(event.orderAddress) && { label: 'Адрес', value: event.orderAddress },
        !isNil(event.orderGeolocation) &&
          !isEmpty(event.orderGeolocation) && {
            label: 'Координаты',
            value: (
              <>
                <Coordinates data={event.orderGeolocation} />
                <IconButton
                  aria-label='Open map in new tab'
                  target='_blank'
                  size='small'
                  href={getGeolocationLink(event.orderGeolocation)}>
                  <OpenInNewIcon fontSize='inherit' />
                </IconButton>
              </>
            ),
          },
        {
          label: 'Клиент',
          value: (
            <MuiLink component={RouterLink} to={`/clients/${event.clientId}`}>
              {event.clientName}
            </MuiLink>
          ),
        },
        {
          label: 'Номер телефона клиента',
          value: (
            <MuiLink href={`tel:+${event.clientPhone}`}>
              {getFormattedRuPhone(event.clientPhone)}
            </MuiLink>
          ),
        },
      ]),
    [event, orderPath]
  );

  return (
    <SimpleDialog
      title={event.eventName}
      open={open}
      onClose={onClose}
      actions={<Button onClick={onClose}>Закрыть</Button>}
      content={
        <ResponsiveKeyValueList
          ListProps={{ disablePadding: true }}
          ListItemProps={{ disableGutters: true }}
          rows={rows}
        />
      }
    />
  );
};

export interface EventDetailDialogProps {
  open: boolean;
  onClose: VoidFunction;
  event: EventExtendedProps;
}

export default EventDetailDialog;
